import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/Card";
import { useState } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DepartureViewProps {
  personId: string;
  personName: string;
  onUndone?: () => void;
}

interface DepartureRecord {
  personId: number;
  exitDate: string;
  destination: string | null;
  reasonForLeaving: string | null;
  outcomeNotes: string | null;
  hideSensitiveData: boolean;
  customFields: {
    fieldId: number;
    fieldLabel: string;
    fieldType: string;
    value: string | null;
    selectedChoices: { id: number; label: string }[];
  }[];
  hadOpenBedAssignment: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DepartureView({
  personId,
  personName,
  onUndone,
}: DepartureViewProps) {
  const { hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [showUndoConfirm, setShowUndoConfirm] = useState(false);

  const isAdmin = hasRole("SYSTEM_ADMIN", "AGENCY_ADMIN");

  const {
    data: departure,
    isLoading,
    error,
  } = useQuery<DepartureRecord>({
    queryKey: ["departure", personId],
    queryFn: async () => {
      const res = await api.get<{ data: DepartureRecord }>(
        `/clients/${personId}/departure`,
      );
      return res.data;
    },
    enabled: !!personId,
  });

  const undoMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/clients/${personId}/departure`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", personId] });
      queryClient.invalidateQueries({
        queryKey: ["departure", personId],
      });
      onUndone?.();
    },
  });

  const handleUndoConfirm = () => {
    setShowUndoConfirm(false);
    undoMutation.mutate();
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-4 p-6">
          <div className="h-6 w-48 animate-pulse rounded bg-muted" />
          <div className="h-4 w-full animate-pulse rounded bg-muted" />
          <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
          <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    );
  }

  if (error || !departure) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-sm text-muted-foreground">
          No departure data available.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5" />
            Departure Details
          </CardTitle>
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
            Exited
          </span>
        </CardHeader>
        <CardContent>
          <dl className="space-y-3 text-sm">
            <DetailRow
              label="Exit Date"
              value={new Date(departure.exitDate).toLocaleDateString()}
            />
            {departure.destination && (
              <DetailRow label="Destination" value={departure.destination} />
            )}
            {departure.reasonForLeaving && (
              <DetailRow
                label="Reason for Leaving"
                value={departure.reasonForLeaving}
              />
            )}
            {departure.outcomeNotes && (
              <DetailRow
                label="Outcome Notes"
                value={departure.outcomeNotes}
              />
            )}
            {departure.hadOpenBedAssignment && (
              <DetailRow
                label="Bed Assignment"
                value="Bed assignment was closed upon departure"
              />
            )}
          </dl>

          {/* Custom departure fields */}
          {departure.customFields.length > 0 && (
            <div className="mt-4 border-t pt-4">
              <h4 className="mb-3 text-sm font-semibold text-foreground">
                Departure Questions
              </h4>
              <dl className="space-y-3 text-sm">
                {departure.customFields.map((field) => (
                  <DetailRow
                    key={field.fieldId}
                    label={field.fieldLabel}
                    value={
                      field.selectedChoices.length > 0
                        ? field.selectedChoices
                            .map((c) => c.label)
                            .join(", ")
                        : field.value ?? "-"
                    }
                  />
                ))}
              </dl>
            </div>
          )}
        </CardContent>

        {isAdmin && (
          <CardFooter className="justify-end">
            <Button
              variant="outline"
              onClick={() => setShowUndoConfirm(true)}
              loading={undoMutation.isPending}
              className="gap-2"
            >
              <Undo2 className="h-4 w-4" />
              Undo Departure
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Undo confirmation dialog */}
      {showUndoConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="mx-4 max-w-md">
            <CardHeader>
              <CardTitle className="text-lg">Undo Departure</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to undo the departure for{" "}
                <span className="font-medium text-foreground">
                  {personName}
                </span>
                ? This will:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-muted-foreground">
                <li>Clear the exit date</li>
                <li>Remove departure details</li>
                {departure.hadOpenBedAssignment && (
                  <li>Reopen the bed assignment</li>
                )}
              </ul>
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowUndoConfirm(false)}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleUndoConfirm}>
                Confirm Undo
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      {/* Error display */}
      {undoMutation.isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          Failed to undo departure. Please try again.
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}
